// Framework + libraries
import { inject } from '@angular/core';
import { combineLatest, switchMap } from 'rxjs';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
// Ours
import { GlobalDivisionStoreState } from '@r1-compass-library/store/divisions/global-divisions-store';
import { DivisionConfigService, IamDivisionService } from '@r1-compass-library/services';
import {
    DivisionBasicSettings,
    DivisionConfig,
    DivisionConfigData,
    DivisionMappingSettings,
    DivisionSalesforceTargetSettings,
} from '@r1-compass-library/models';
import { withLoading } from '@r1-compass-library/store/_features/store-helpers';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';

export function withSyncedGlobalDivisionMethods<_>(initialState: GlobalDivisionStoreState) {
    return signalStoreFeature(
        {
            state: type<GlobalDivisionStoreState>(),
        },
        withLoading(),
        withMethods(
            (
                store,
                iamDivisionService = inject(IamDivisionService),
                divisionConfigService = inject(DivisionConfigService)
            ) => {
                const setAllDivisionConfigs = (configs: DivisionConfig[]): void => {
                    store.setLoading(false);
                    patchState(store, { allDivisionConfigs: configs });
                };
                const handleCreateDivisionConfigAndData = (
                    divisionConfig: Partial<DivisionConfig>,
                    data: {
                        mapping: DivisionBasicSettings;
                    }
                ) => {
                    const config = { ...divisionConfig, config: data };
                    return divisionConfigService.create(config);
                };
                const handleUpdateDivisionConfigAndData = (
                    divisionId: number,
                    divisionConfigToSave: Partial<DivisionConfig>,
                    data:
                        | Partial<DivisionConfigData>
                        | {
                              mapping: DivisionBasicSettings;
                          }
                        | { mapping: DivisionBasicSettings & DivisionSalesforceTargetSettings }
                ) => {
                    //we update one at a time, starting with data, in order to prevent a race condition in the database persistence layer due to how
                    //the merging of our data works currently
                    return divisionConfigService
                        .update(divisionId, divisionConfigToSave)
                        .pipe(switchMap(() => divisionConfigService.updateData(divisionId, data)));
                };
                const handleUpdateDivisionConfig = (
                    divisionId: number,
                    divisionConfigToSave: Partial<DivisionConfig>
                ) => {
                    return divisionConfigService.update(divisionId, divisionConfigToSave);
                };

                return {
                    getAllDivisionData: rxMutation({
                        operation: (_: void) => {
                            store.setLoading(true);
                            return combineLatest([iamDivisionService.findAll(), divisionConfigService.findAll()]);
                        },
                        operator: exhaustOp,
                        onSuccess: ([divisions, configs]) => {
                            store.setLoading(false);
                            patchState(store, { allIamDivisions: divisions });
                            patchState(store, { allDivisionConfigs: configs });
                        },
                        onError: err => console.error(err),
                    }),
                    saveDivisionConfigAndDataBasicSettings: rxMutation({
                        operation: (args: {
                            config: Partial<DivisionConfig>;
                            data:
                                | {
                                      mapping: DivisionMappingSettings; // existing mappings save
                                  }
                                | {
                                      mapping: DivisionBasicSettings; // create save
                                  };
                        }) => {
                            store.setLoading(true);
                            const divisionConfigCall = args.config.id
                                ? handleUpdateDivisionConfigAndData(args.config.id, args.config, args.data)
                                : handleCreateDivisionConfigAndData(args.config, args.data);
                            return divisionConfigCall;
                        },
                        operator: exhaustOp,
                        onSuccess: savedConfig => {
                            setAllDivisionConfigs(
                                store.allDivisionConfigs().map(cachedConfig => {
                                    return cachedConfig.id === savedConfig.id ? savedConfig : cachedConfig;
                                })
                            );
                        },
                        onError: err => console.error(err),
                    }),
                    saveDivisionConfigAndData: rxMutation({
                        operation: (args: { config: Partial<DivisionConfig>; data: Partial<DivisionConfigData> }) => {
                            store.setLoading(true);
                            const divisionConfigCall = handleUpdateDivisionConfigAndData(
                                args.config.id!,
                                args.config,
                                args.data
                            );
                            return divisionConfigCall;
                        },
                        operator: exhaustOp,
                        onSuccess: savedConfig => {
                            setAllDivisionConfigs(
                                store.allDivisionConfigs().map(cachedConfig => {
                                    return cachedConfig.id === savedConfig.id ? savedConfig : cachedConfig;
                                })
                            );
                        },
                        onError: err => console.error(err),
                    }),
                    saveDivisionConfig: rxMutation({
                        operation: (args: { config: Partial<DivisionConfig> }) => {
                            store.setLoading(true);
                            return handleUpdateDivisionConfig(args.config.id!, args.config);
                        },
                        operator: exhaustOp,
                        onSuccess: savedConfig => {
                            setAllDivisionConfigs(
                                store.allDivisionConfigs().map(cachedConfig => {
                                    return cachedConfig.id === savedConfig.id ? savedConfig : cachedConfig;
                                })
                            );
                        },
                        onError: err => console.error(err),
                    }),
                    resetState() {
                        patchState(store, initialState);
                    },
                };
            }
        )
    );
}
